import React, { useRef, useState } from 'react';
import { ClientOnly } from 'remix-utils';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPagination,
    CarouselPrevious,
    CarouselNext,
} from '@rocco/components/carousel';
import { Image } from '@rocco/components/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ReactComponent as Files } from '../../../icons/svg/layer-icon.svg';
import { ReactComponent as ArrowRight } from '../../../icons/svg/arrow-r.svg';
import type { VideoPlayerError } from '@rocco/components/video-player/video-player';
import { VideoPlayer } from '@rocco/components/video-player/video-player';
import { useLogger } from '@archipro-website/logger/client';
import { CarouselOptions } from '@rocco/components/carousel/carousel';
import { cn } from '@rocco/utils/cn';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useABTestIsOn } from '../../../../../web/app/modules/root/hook/use-growthbook';
import { useLocation } from '@remix-run/react';

import { disableSliderAtom } from '@rocco/states/disableSliderAtom';
import { isSafari, isMobile } from 'react-device-detect';

export interface ImageModel {
    src: string;
    alt?: string;
    multiplied?: boolean;
}

export const _tileMediaVariants = cva('w-full', {
    variants: {
        aspectRatio: {
            featured: 'h-full', // there's no aspect ratio for featured tile - it should take all available space in the grid
            default: 'aspect-tile-default',
            article: 'aspect-tile-article',
            product: 'aspect-tile-product',
            category: 'aspect-tile-category',
            professional: 'aspect-tile-professional',
            square: 'aspect-square',
        },
        rounded: {
            true: 'overflow-hidden rounded-tile',
            false: '',
        },
    },
    defaultVariants: {
        aspectRatio: 'default',
        rounded: true,
    },
});

const _tileMediaSizes = cva('', {
    variants: {
        sizes: {
            // yep, it works with any strings not just css classes :)
            featured:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            default:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            article:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            product:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            professional:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 24vw, 100vw',
            category:
                '(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw',
            square: '(min-width: 1921px) 348px, ((min-width: 1024px) and (max-width: 1920px)) 18vw, 49vw',
        },
    },
});

const _tileMediaImageVariants = cva('relative h-full overflow-hidden', {
    variants: {
        gradiantOverlay: {
            true: 'after:absolute after:inset-0 after:block after:bg-gradient-to-b after:from-transparent after:to-black after:opacity-60',
        },
        multiplied: {
            true: 'bg-[#f2f1f0]',
        },
    },
    defaultVariants: {
        gradiantOverlay: false,
        multiplied: false,
    },
});

export type TileMediaAspectRatio = VariantProps<
    typeof _tileMediaVariants
>['aspectRatio'];

interface TileMediaImageProps {
    images: ImageModel[];
    imagesLimit: number;
    fallbackAlt?: string;
    gradiantOverlay?: boolean;
    onSlideChange?: (
        direction: 'CarouselPrev' | 'CarouselNext',
        currentIndex: number
    ) => void;
}

interface TileMediaVideoProps {
    video: string[];
    fallbackImage?: ImageModel;
}

interface TileMediaProps
    extends Partial<TileMediaImageProps>,
        Partial<TileMediaVideoProps>,
        VariantProps<typeof _tileMediaVariants>,
        VariantProps<typeof _tileMediaSizes> {}

// TileMedia component to display either images or video based on the provided props
export const TileMedia = (props: TileMediaProps) => {
    const isTileMediaSlider = useABTestIsOn('tile-media-slider');

    const {
        aspectRatio,
        images,
        imagesLimit = isTileMediaSlider ? (images?.length ?? 0) : 5,
        video,
        fallbackAlt,
        gradiantOverlay,
        onSlideChange,
        rounded,
    } = props;

    const { disableSlider, showMobileSearchResultsAll } =
        useAtomValue(disableSliderAtom);
    const shouldLimitImages = disableSlider && showMobileSearchResultsAll;

    const sizes = _tileMediaSizes({ sizes: aspectRatio });

    return (
        <div
            className={cn(
                _tileMediaVariants({ aspectRatio, rounded }),
                'rocco-tile-media'
            )}
        >
            {video?.length ? (
                <TileMediaVideo video={video} fallbackImage={images?.[0]} />
            ) : !shouldLimitImages && images && images.length > 1 ? (
                <TileMediaImageSlider
                    images={images}
                    imagesLimit={imagesLimit}
                    fallbackAlt={fallbackAlt}
                    onSlideChange={onSlideChange}
                    sizes={sizes}
                />
            ) : (
                // Some tiles may not have images, e.g. not live items.
                images?.[0] && (
                    <TileMediaImage
                        image={images[0]}
                        fallbackAlt={fallbackAlt}
                        gradiantOverlay={gradiantOverlay}
                        sizes={sizes}
                        eager={!isMobile && isSafari}
                    />
                )
            )}
        </div>
    );
};

const TileMediaImage = ({
    image,
    fallbackAlt,
    gradiantOverlay,
    eager,
    className,
    sizes,
}: {
    image: ImageModel;
    fallbackAlt?: string;
    gradiantOverlay?: boolean;
    eager?: boolean;
    className?: string;
    sizes?: string;
}) => {
    return (
        <div
            className={cn(
                _tileMediaImageVariants({
                    gradiantOverlay,
                    multiplied: image.multiplied,
                }),
                className
            )}
        >
            <Image
                src={image.src}
                alt={image.alt ?? fallbackAlt ?? ''}
                fill={true}
                loading={eager ? 'eager' : 'lazy'}
                fit={image.multiplied ? 'contain' : 'cover'}
                multiplied={image.multiplied}
                sizes={sizes}
            />
        </div>
    );
};

const TileMediaImageSlider = ({
    images,
    imagesLimit,
    onSlideChange,
    sizes,
}: TileMediaImageProps & { sizes?: string }) => {
    const location = useLocation();
    const isProductsPage = location.pathname === '/products';
    // Preload the first two images
    const [loadedImageIndexes, setLoadedImageIndexes] = useState<number[]>([
        0, 1,
    ]);

    const isTileMediaSlider = useABTestIsOn('tile-media-slider');
    const stagedIndex = useRef<number>(0);
    const totalImages = images.length; // Add this line to hold total images
    const imagesCount = Math.min(images.length, imagesLimit);
    const [paginationCount, setPaginationCount] = useState<string>(
        `${stagedIndex.current + 1} / ${totalImages}`
    );

    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);

    const carouselOptions: CarouselOptions = {
        loop: isTileMediaSlider ? true : false,
        watchDrag: (api, evt) => {
            // Only allow dragging when the target is directly in this carousel
            // and not part of a parent carousel drag
            const target = evt.target as Element;
            const carouselElement = api.containerNode();

            return (
                carouselElement?.contains(target) &&
                target.closest('.slider-item-image') !== null
            );
        },
    };

    const [isAtStart, setIsAtStart] = useState(false);
    const [isAtEnd, setIsAtEnd] = useState(false);

    const handleSlideChange = ({ currentIndex }: { currentIndex: number }) => {
        const isAtStart = currentIndex === 0;
        const isAtEnd = currentIndex === imagesCount - 1;

        setIsAtStart(isAtStart);
        setIsAtEnd(isAtEnd);

        const indexesToLoad = [
            currentIndex,
            isAtStart ? null : currentIndex - 1,
            isAtEnd ? null : currentIndex + 1,
        ].filter((i): i is number => i !== null && i >= 0 && i < imagesCount);

        // Only update if new indexes are needed
        setLoadedImageIndexes((prev) => {
            const toAdd = indexesToLoad.filter((i) => !prev.includes(i));

            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });

        // Optional direction callback
        if (onSlideChange) {
            let direction: 'CarouselNext' | 'CarouselPrev' | undefined;

            if (currentIndex > stagedIndex.current) direction = 'CarouselNext';
            else if (currentIndex < stagedIndex.current)
                direction = 'CarouselPrev';
            else if (
                stagedIndex.current === imagesCount - 1 &&
                currentIndex === 0
            )
                direction = 'CarouselNext';
            else if (
                stagedIndex.current === 0 &&
                currentIndex === imagesCount - 1
            )
                direction = 'CarouselPrev';

            stagedIndex.current = currentIndex;
            if (direction) onSlideChange(direction, currentIndex);
            isTileMediaSlider &&
                setPaginationCount(`${currentIndex + 1}/${imagesCount}`);
        }
    };

    // A/B test variant
    if (isTileMediaSlider) {
        // AB Test variant A
        return (
            <div className="group h-full">
                <Carousel
                    options={{ ...carouselOptions }}
                    onChangeSlide={handleSlideChange}
                >
                    <CarouselContent>
                        {images.slice(0, imagesLimit).map((image, index) => {
                            return (
                                <CarouselItem key={index}>
                                    {loadedImageIndexes.includes(index) && (
                                        <TileMediaImage
                                            image={image}
                                            eager={true}
                                            className="slider-item-image relative"
                                            sizes={sizes}
                                        />
                                    )}
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>

                    {isDesktop ? (
                        <div className="absolute bottom-4 flex w-full justify-between px-4 opacity-0 transition-opacity duration-300 ease-linear md:group-hover:opacity-100">
                            <div className="flex flex-1 justify-start">
                                <CarouselPrevious
                                    size="square36"
                                    color="secondary70"
                                >
                                    <ChevronLeft className="mr-0.5" />
                                </CarouselPrevious>
                            </div>

                            <div className="mt-1.5 flex">
                                <span className="mr-3 flex items-center text-[10px] text-white">
                                    {paginationCount}
                                </span>
                                <CarouselPagination />
                            </div>

                            <div className="flex flex-1 justify-end">
                                <CarouselNext
                                    size="square36"
                                    color="secondary70"
                                >
                                    <ChevronRight className="ml-0.5" />
                                </CarouselNext>
                            </div>
                        </div>
                    ) : (
                        <div className="relative">
                            <div className="absolute bottom-4 w-full px-4">
                                <div className="flex items-center">
                                    <span className="flex items-center text-[0.75rem] text-white">
                                        <Files className="mr-1 size-4" />
                                        {paginationCount}
                                    </span>
                                </div>

                                {/* Centered pagination */}
                                <div className="absolute bottom-1.5 left-1/2 flex -translate-x-1/2 justify-center">
                                    <CarouselPagination />
                                </div>
                            </div>
                        </div>
                    )}
                </Carousel>
            </div>
        );
    } else {
        // AB Test variant B
        return (
            <div className="group h-full">
                <Carousel
                    options={{ ...carouselOptions }}
                    onChangeSlide={handleSlideChange}
                >
                    <CarouselContent>
                        {images.slice(0, imagesLimit).map((image, index) => {
                            const isLastImage =
                                totalImages > 5 && index === imagesLimit - 1;
                            return (
                                <CarouselItem key={index}>
                                    {loadedImageIndexes.includes(index) && (
                                        <div className="relative h-full w-full">
                                            <TileMediaImage
                                                image={image}
                                                eager={true}
                                                className={`slider-item-image relative ${isLastImage && index === 4 ? 'bg-black/80 shadow-[8px_10px_8px_rgba(0,0,0,0.25)] backdrop-blur-md' : ''}`}
                                                sizes={sizes}
                                            />
                                            {isLastImage && index === 4 && (
                                                <div
                                                    className={`pointer-events-none absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center bg-black/80 text-white`}
                                                >
                                                    <div className="flex items-center justify-center">
                                                        <span
                                                            className={cn(
                                                                'font-medium tracking-[0.8px]',
                                                                isProductsPage
                                                                    ? isDesktop
                                                                        ? 'text-[1.5rem] leading-[0.5rem]'
                                                                        : 'text-[1.125rem] leading-[0.375rem]'
                                                                    : 'text-[1.5rem] leading-[0.5rem]'
                                                            )}
                                                        >
                                                            View&nbsp;more
                                                        </span>
                                                        <span
                                                            className={cn(
                                                                'ml-[0.6rem]',
                                                                isProductsPage
                                                                    ? isDesktop
                                                                        ? 'w-[1.5876rem]'
                                                                        : 'w-[1.25rem]'
                                                                    : 'w-[1.5876rem]'
                                                            )}
                                                        >
                                                            <ArrowRight className="w-full fill-white" />
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>
                    {isDesktop ? (
                        <div className="absolute bottom-4 flex w-full justify-between px-4 opacity-0 transition-opacity duration-300 ease-linear md:group-hover:opacity-100">
                            <div className="flex flex-1 justify-start">
                                <CarouselPrevious
                                    size="square36"
                                    color="secondary70"
                                    className={cn(
                                        'transition-opacity duration-200',
                                        isAtStart && 'opacity-0'
                                    )}
                                >
                                    <ChevronLeft className="mr-0.5" />
                                </CarouselPrevious>
                            </div>

                            <div className="mt-1.5 flex">
                                <CarouselPagination />
                            </div>

                            <div className="flex flex-1 justify-end">
                                <CarouselNext
                                    size="square36"
                                    color="secondary70"
                                    className={cn(
                                        'transition-opacity duration-200',
                                        isAtEnd && 'opacity-0'
                                    )}
                                >
                                    <ChevronRight className="ml-0.5" />
                                </CarouselNext>
                            </div>
                        </div>
                    ) : (
                        <div className="absolute bottom-4 flex w-full justify-center px-4">
                            <div className="mt-1.5 flex">
                                <CarouselPagination />
                            </div>
                        </div>
                    )}
                </Carousel>
            </div>
        );
    }
};

const TileMediaVideo = ({ video, fallbackImage }: TileMediaVideoProps) => {
    const logger = useLogger();

    return (
        <div className="relative h-full overflow-hidden rounded-lg">
            <ClientOnly
                fallback={
                    fallbackImage && <TileMediaImage image={fallbackImage} />
                }
            >
                {() => (
                    <VideoPlayer
                        src={video}
                        fill={true}
                        playsInline={true}
                        controls={false}
                        mode="auto"
                        loop={true}
                        onError={(e?: VideoPlayerError) => {
                            const errorInfo = !e
                                ? 'unknown'
                                : typeof e === 'string'
                                  ? e
                                  : `${e.name}: ${e.message}`;

                            logger.error(
                                `Error while retrieving video. Error: ${errorInfo}`
                            );
                        }}
                    />
                )}
            </ClientOnly>
        </div>
    );
};
